import { motion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    MdSwapHoriz,
    MdHistory,
    MdTrendingUp,
    MdAccountBalanceWallet,
    MdArrowDownward,
    MdWarningAmber,
    MdMonetizationOn,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import toast from "react-hot-toast";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const SectionCard = ({ styles, children, className = "" }) => (
    <div className={`p-3 ${className}`} style={styles.card}>
        {children}
    </div>
);

const BalancePill = ({ label, value }) => (
    <div
        className="text-center flex-fill"
        style={{
            borderRadius: "12px",
            padding: "10px 6px",
            minHeight: "58px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            background: "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)",
        }}
    >
        <div className="fw-bold text-white" style={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
            {value}
        </div>
        <div
            className="text-white text-uppercase mt-1"
            style={{ fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.05em", opacity: 0.88 }}
        >
            {label}
        </div>
    </div>
);

const UsdtSwap = ({ theme = "dark" }) => {
    const [activeTab, setActiveTab] = useState("usdt to coin");
    const [inputAmount, setInputAmount] = useState("");
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [coinRate, setCoinRate] = useState(0);
    const [userBalance, setUserBalance] = useState({
        totalCoins: 0,
        lockedCoins: 0,
        swapableCoins: 0,
        usdtBalance: 0,
        lockUsdt: 0,
        withdrawableUsdt: 0,
    });
    const [swapReport, setSwapReport] = useState([]);
    const [isLoadingSwapReport, setIsLoadingSwapReport] = useState(false);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const maxChip = useMemo(
        () => ({
            borderRadius: "8px",
            padding: "6px 12px",
            fontSize: "0.75rem",
            fontWeight: 700,
            border: `1px solid ${isDark ? "#0d9488" : "#99f6e4"}`,
            backgroundColor: isDark ? "rgba(20, 184, 166, 0.15)" : "#ccfbf1",
            color: isDark ? "#5eead4" : "#0f766e",
        }),
        [isDark]
    );

    const receiveBox = useMemo(
        () => ({
            backgroundColor: isDark ? "rgba(34, 197, 94, 0.12)" : "#ecfdf5",
            border: `1px solid ${isDark ? "rgba(34, 197, 94, 0.35)" : "#a7f3d0"}`,
            borderRadius: "10px",
            color: isDark ? "#4ade80" : "#15803d",
        }),
        [isDark]
    );

    const swapSubmitBtn = useMemo(
        () => ({
            borderRadius: "12px",
            padding: "14px",
            fontWeight: 700,
            fontSize: "1rem",
            border: "none",
            background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
            color: "#fff",
            boxShadow: "0 6px 20px rgba(20, 184, 166, 0.4)",
        }),
        []
    );

    useEffect(() => {
        setInputAmount("");
    }, [activeTab]);

    const calculateOutput = () => {
        if (!inputAmount || isNaN(parseFloat(inputAmount)) || coinRate === 0) return "0.00";
        const amount = parseFloat(inputAmount);
        if (activeTab === "usdt to coin") {
            return (amount * coinRate).toFixed(2);
        }
        return (amount / coinRate).toFixed(2);
    };

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                const usdtBalance = parseFloat(data.data.usdt) || 0;
                const lockUsdtVal = parseFloat(data.data.lock_usdt) || 0;
                const parsedWithdrawable = parseFloat(data.data.withdrawable_usdt);
                const withdrawableUsdtVal = !isNaN(parsedWithdrawable) ? parsedWithdrawable : usdtBalance;
                setUserBalance({
                    totalCoins: parseFloat(data.data.balance) || 0,
                    usdtBalance,
                    lockedCoins: parseFloat(data.data.lock_balance) || 0,
                    swapableCoins:
                        parseFloat(data.data.balance) - parseFloat(data.data.lock_balance) || 0,
                    lockUsdt: lockUsdtVal,
                    withdrawableUsdt: withdrawableUsdtVal,
                });
                TriggerBalanceUpdate({
                    balance: data?.data?.balance,
                    usdt: data?.data?.usdt,
                });
            }
        } catch (error) {
            console.log("Error fetching profile:", error);
            toast.error("Failed to load balance information");
        }
    };

    const FetchCoinRate = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/coin-rate`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.coin) {
                setCoinRate(parseFloat(data.coin));
            }
        } catch (error) {
            console.log("Error fetching coin rate:", error);
            toast.error("Failed to load coin rate");
        }
    };

    const FetchSwapReport = async () => {
        setIsLoadingSwapReport(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/swap-report`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    console.error(data?.error);
                }
            }

            if (data?.data) {
                setSwapReport(data.data.slice(0, 5));
            }
        } catch (error) {
            console.log("Error fetching swap report:", error);
        } finally {
            setIsLoadingSwapReport(false);
        }
    };

    useEffect(() => {
        FetchProfile();
        FetchCoinRate();
        FetchSwapReport();
    }, []);

    const handleMaxClick = () => {
        if (activeTab === "usdt to coin") {
            const max = userBalance.usdtBalance ?? 0;
            setInputAmount((parseFloat(max) || 0).toFixed(2));
        } else {
            const max = userBalance.swapableCoins ?? 0;
            setInputAmount((parseFloat(max) || 0).toFixed(2));
        }
    };

    const handleSwap = () => {
        if (!inputAmount || parseFloat(inputAmount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        const amount = parseFloat(inputAmount);
        if (amount < 0) {
            toast.error("Amount cannot be negative");
            return;
        }

        const maxCoins = userBalance.swapableCoins ?? 0;
        if (activeTab === "usdt to coin" && amount > userBalance.usdtBalance) {
            toast.error("Insufficient USDT balance");
            return;
        }

        if (activeTab === "coin to usdt" && amount > maxCoins) {
            toast.error("Insufficient swapable coins");
            return;
        }

        if (activeTab === "usdt to coin" && amount < 1) {
            toast.error("Minimum swap amount is 1 USDT");
            return;
        }

        if (activeTab === "coin to usdt" && amount < 100) {
            toast.error("Minimum swap amount is 100 coins");
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmSwap = async () => {
        setIsProcessing(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/payment/swap`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                type: activeTab,
                amount: inputAmount,
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            } else if (data?.msg) {
                toast.success(data?.msg);
                setInputAmount("");
                FetchProfile();
                FetchSwapReport();
            }
        } catch (error) {
            console.log("Swap error:", error);
            toast.error("Failed to process swap");
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
        }
    };

    const formatNumber = (num) => {
        const n = parseFloat(num);
        if (isNaN(n)) return "0.00";
        return n.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const getUSDTPerCoin = () => {
        if (coinRate === 0) return "0";
        return (1 / coinRate).toFixed(4);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleViewAllSwapReport = () => {
        navigate("/swap-report");
    };

    const isUsdtToCoin = activeTab === "usdt to coin";
    const canSubmit =
        inputAmount &&
        parseFloat(inputAmount) > 0 &&
        !isProcessing &&
        coinRate > 0;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div
                variants={MOTION.container}
                initial="hidden"
                animate="visible"
                className="d-flex flex-column gap-3"
            >
                {/* Hero */}
                <motion.div variants={MOTION.item} style={styles.hero(styles.heroTeal)}>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <MdSwapHoriz size={28} />
                            <div>
                                <h5 className="fw-bold mb-0">Currency Swap</h5>
                                <p className="mb-0 small opacity-75">USDT ↔ Coins instantly</p>
                            </div>
                        </div>
                        <motion.button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1"
                            style={{
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.3)",
                                color: "#fff",
                                borderRadius: "10px",
                                fontWeight: 600,
                                fontSize: "0.72rem",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleViewAllSwapReport}
                        >
                            <MdHistory size={16} />
                            Report
                        </motion.button>
                    </div>

                    <div
                        className="text-center mb-3 py-2 rounded-3"
                        style={{ background: "rgba(255,255,255,0.12)" }}
                    >
                        <p
                            className="mb-1 small text-uppercase opacity-75"
                            style={{ letterSpacing: "0.08em", fontWeight: 600 }}
                        >
                            Live rate
                        </p>
                        <div className="fw-bold" style={{ fontSize: "1.1rem" }}>
                            1 USDT = {coinRate || "—"} Coins
                        </div>
                        <small className="opacity-75">1 Coin = ${getUSDTPerCoin()} USDT</small>
                    </div>

                    <p className="mb-2 small text-uppercase opacity-75" style={{ letterSpacing: "0.06em", fontWeight: 600 }}>
                        Coins
                    </p>
                    <div className="d-flex gap-2 mb-2">
                        <BalancePill label="Total" value={formatNumber(userBalance.totalCoins)} />
                        <BalancePill label="Locked" value={formatNumber(userBalance.lockedCoins)} />
                        <BalancePill label="Swapable" value={formatNumber(userBalance.swapableCoins)} />
                    </div>
                    <p className="mb-2 small text-uppercase opacity-75" style={{ letterSpacing: "0.06em", fontWeight: 600 }}>
                        USDT
                    </p>
                    <div className="d-flex gap-2">
                        <BalancePill label="Total" value={`$${formatNumber(userBalance.usdtBalance)}`} />
                        <BalancePill label="Locked" value={`$${formatNumber(userBalance.lockUsdt)}`} />
                        <BalancePill label="Withdraw" value={`$${formatNumber(userBalance.withdrawableUsdt)}`} />
                    </div>
                </motion.div>

                {/* Swap form */}
                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles} className="p-0">
                        <div className="d-flex gap-2 p-3 pb-0">
                            <motion.button
                                type="button"
                                className="btn flex-fill"
                                style={styles.filterChip(isUsdtToCoin)}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setActiveTab("usdt to coin")}
                            >
                                USDT → Coins
                            </motion.button>
                            <motion.button
                                type="button"
                                className="btn flex-fill"
                                style={styles.filterChip(!isUsdtToCoin)}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setActiveTab("coin to usdt")}
                            >
                                Coins → USDT
                            </motion.button>
                        </div>

                        <div className="p-3">
                            <p className="mb-2" style={styles.sectionTitle}>
                                You pay
                            </p>
                            <div
                                className="d-flex align-items-center gap-2 mb-2 px-3 py-2"
                                style={{ ...styles.input, minHeight: "52px" }}
                            >
                                <span
                                    className="fw-bold flex-shrink-0"
                                    style={{
                                        fontSize: "1.2rem",
                                        color: isUsdtToCoin ? "#14b8a6" : "#f59e0b",
                                    }}
                                >
                                    {isUsdtToCoin ? "$" : "🪙"}
                                </span>
                                <input
                                    type="number"
                                    className="form-control border-0 shadow-none flex-grow-1 fw-bold usdt-swap-amount-input"
                                    placeholder="0.00"
                                    value={inputAmount}
                                    min="0"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const num = parseFloat(val);
                                        if (val === "" || (!isNaN(num) && num >= 0)) {
                                            setInputAmount(val);
                                        }
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    onKeyDown={(e) => {
                                        if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                                    }}
                                    style={{
                                        fontSize: "1.25rem",
                                        outline: "none",
                                        backgroundColor: "transparent",
                                        color: isDark ? "#f9fafb" : "#111827",
                                        minWidth: 0,
                                        padding: "8px 4px",
                                    }}
                                />
                                <motion.button
                                    type="button"
                                    className="btn flex-shrink-0"
                                    style={maxChip}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={handleMaxClick}
                                >
                                    MAX
                                </motion.button>
                            </div>
                            <div className="d-flex flex-wrap justify-content-between gap-1 mb-3">
                                <small style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                    Available:{" "}
                                    {isUsdtToCoin
                                        ? `$${formatNumber(userBalance.usdtBalance)}`
                                        : `${formatNumber(userBalance.swapableCoins)} coins`}
                                </small>
                                <small style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                    Min: {isUsdtToCoin ? "1 USDT" : "100 coins"}
                                </small>
                            </div>

                            <div className="text-center my-2">
                                <motion.div
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        background: isDark ? "#0f172a" : "#f1f5f9",
                                        border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                                        color: "#14b8a6",
                                    }}
                                    animate={{ rotate: isUsdtToCoin ? 0 : 180 }}
                                    transition={{ duration: 0.35 }}
                                >
                                    <MdArrowDownward size={22} />
                                </motion.div>
                            </div>

                            <p className="mb-2" style={styles.sectionTitle}>
                                You receive
                            </p>
                            <div
                                className="d-flex align-items-center gap-2 px-3 py-3 fw-bold"
                                style={{ ...receiveBox, fontSize: "1.25rem", minHeight: "52px" }}
                            >
                                <span className="flex-shrink-0" style={{ fontSize: "1.2rem" }}>
                                    {isUsdtToCoin ? "🪙" : "$"}
                                </span>
                                <span className="flex-grow-1">{calculateOutput()}</span>
                                <span className="small flex-shrink-0 opacity-75">
                                    {isUsdtToCoin ? "Coins" : "USDT"}
                                </span>
                            </div>

                            <div className="mt-3 p-2 rounded text-center" style={styles.notice}>
                                <MdTrendingUp className="me-1" size={16} />
                                <span style={{ fontSize: "0.78rem" }}>
                                    1 USDT = {coinRate} Coins · 0% swap fee
                                </span>
                            </div>
                        </div>
                    </SectionCard>
                </motion.div>

                {/* Preview */}
                {inputAmount && parseFloat(inputAmount) > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        <SectionCard styles={styles}>
                            <p className="mb-3" style={styles.sectionTitle}>
                                Swap preview
                            </p>
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.85rem" }}>
                                <span style={{ color: styles.textMuted }}>You pay</span>
                                <span className="fw-semibold">
                                    {isUsdtToCoin
                                        ? `$${formatNumber(inputAmount)} USDT`
                                        : `${formatNumber(inputAmount)} Coins`}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.85rem" }}>
                                <span style={{ color: styles.textMuted }}>You receive</span>
                                <span className="fw-bold" style={{ color: "#22c55e" }}>
                                    {isUsdtToCoin
                                        ? `${formatNumber(calculateOutput())} Coins`
                                        : `$${formatNumber(calculateOutput())} USDT`}
                                </span>
                            </div>
                            <div
                                className="pt-2 mt-2 d-flex justify-content-between fw-bold"
                                style={{
                                    borderTop: `1px solid ${styles.listBorder}`,
                                    fontSize: "0.9rem",
                                }}
                            >
                                <span>Total</span>
                                <span style={{ color: "#22c55e" }}>
                                    {isUsdtToCoin
                                        ? `${formatNumber(calculateOutput())} Coins`
                                        : `$${formatNumber(calculateOutput())} USDT`}
                                </span>
                            </div>
                            <small className="d-block mt-2" style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                No swap charges applied
                            </small>
                        </SectionCard>
                    </motion.div>
                )}

                {/* Submit */}
                <motion.div variants={MOTION.item}>
                    <motion.button
                        type="button"
                        className="btn w-100"
                        style={{
                            ...swapSubmitBtn,
                            opacity: canSubmit ? 1 : 0.55,
                            boxShadow: canSubmit ? swapSubmitBtn.boxShadow : "none",
                        }}
                        whileHover={canSubmit ? { scale: 1.02 } : {}}
                        whileTap={canSubmit ? { scale: 0.98 } : {}}
                        onClick={handleSwap}
                        disabled={!canSubmit}
                    >
                        {isProcessing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing…
                            </>
                        ) : coinRate === 0 ? (
                            "Loading rates…"
                        ) : (
                            <>
                                <MdSwapHoriz className="me-1" size={20} />
                                Confirm swap
                            </>
                        )}
                    </motion.button>
                </motion.div>

                {/* Recent swaps */}
                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <p className="mb-0" style={styles.sectionTitle}>
                                Recent swaps
                            </p>
                            <motion.button
                                type="button"
                                className="btn btn-sm"
                                style={styles.outlineBtn}
                                whileTap={{ scale: 0.96 }}
                                onClick={handleViewAllSwapReport}
                            >
                                View all
                            </motion.button>
                        </div>

                        {isLoadingSwapReport ? (
                            <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm text-info" role="status" />
                                <p className="mb-0 mt-2 small" style={{ color: styles.textMuted }}>
                                    Loading history…
                                </p>
                            </div>
                        ) : swapReport.length > 0 ? (
                            <div>
                                {swapReport.map((swap, index) => {
                                    const toCoin = swap.type === "usdt to coin";
                                    return (
                                        <div
                                            key={index}
                                            className="d-flex justify-content-between align-items-center py-3"
                                            style={{
                                                borderBottom:
                                                    index === swapReport.length - 1
                                                        ? "none"
                                                        : `1px solid ${styles.listBorder}`,
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        background: toCoin
                                                            ? isDark
                                                                ? "rgba(20, 184, 166, 0.2)"
                                                                : "#ccfbf1"
                                                            : isDark
                                                              ? "rgba(99, 102, 241, 0.2)"
                                                              : "#eef2ff",
                                                        color: toCoin ? "#14b8a6" : "#6366f1",
                                                    }}
                                                >
                                                    {toCoin ? (
                                                        <MdMonetizationOn size={18} />
                                                    ) : (
                                                        <MdAccountBalanceWallet size={18} />
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                                                        {toCoin ? "USDT → Coins" : "Coins → USDT"}
                                                    </div>
                                                    <small style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                                        {formatDate(swap.create_date)}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className="fw-bold" style={{ color: "#22c55e", fontSize: "0.85rem" }}>
                                                    {toCoin
                                                        ? `+${formatNumber(swap.coin)}`
                                                        : `+$${formatNumber(swap.usdt)}`}
                                                </div>
                                                <small style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                                    {toCoin
                                                        ? `$${formatNumber(swap.usdt)} paid`
                                                        : `${formatNumber(swap.coin)} paid`}
                                                </small>
                                            </div>
                                        </div>
                                    );
                                })}
                                <p className="text-center mb-0 mt-2 small" style={{ color: styles.textMuted }}>
                                    Showing 5 most recent
                                </p>
                            </div>
                        ) : (
                            <div style={styles.empty}>No swap history yet</div>
                        )}
                    </SectionCard>
                </motion.div>

                {/* Info */}
                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <p className="mb-2" style={styles.sectionTitle}>
                            Swap information
                        </p>
                        <ul
                            className="mb-0 ps-3"
                            style={{
                                color: styles.textMuted,
                                fontSize: "0.78rem",
                                lineHeight: 1.7,
                            }}
                        >
                            <li>Rate: 1 USDT = {coinRate} Coins (updates automatically)</li>
                            <li>0% fees — no network charges for internal swaps</li>
                            <li>Minimum: 1 USDT or 100 coins</li>
                            <li>Instant processing; locked coins cannot be swapped</li>
                        </ul>
                    </SectionCard>
                </motion.div>
            </motion.div>

            {/* Confirm modal */}
            {showConfirmModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(4px)",
                    }}
                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered mx-3"
                        style={{ maxWidth: "400px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0" style={styles.modal}>
                            <div
                                className="modal-header border-0 px-4 pt-4 pb-0"
                                style={{ background: "transparent" }}
                            >
                                <h6 className="modal-title fw-bold">Confirm swap</h6>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                                    disabled={isProcessing}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body px-4 py-3">
                                {isProcessing ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-info mb-3" role="status" />
                                        <p className="mb-0" style={{ color: styles.textMuted }}>
                                            Processing your swap…
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className="text-center p-3 rounded mb-2"
                                            style={styles.input}
                                        >
                                            <div className="fs-5 fw-bold" style={{ color: "#14b8a6" }}>
                                                {isUsdtToCoin
                                                    ? `$${formatNumber(inputAmount)} USDT`
                                                    : `${formatNumber(inputAmount)} Coins`}
                                            </div>
                                            <small style={{ color: styles.textMuted }}>You pay</small>
                                        </div>
                                        <div className="text-center my-1" style={{ color: "#14b8a6" }}>
                                            <MdArrowDownward size={24} />
                                        </div>
                                        <div
                                            className="text-center p-3 rounded mb-3"
                                            style={receiveBox}
                                        >
                                            <div className="fs-5 fw-bold">
                                                {isUsdtToCoin
                                                    ? `${formatNumber(calculateOutput())} Coins`
                                                    : `$${formatNumber(calculateOutput())} USDT`}
                                            </div>
                                            <small style={{ opacity: 0.85 }}>You receive</small>
                                        </div>

                                        <div
                                            className="p-3 rounded mb-3"
                                            style={{
                                                backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                                                border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                                                fontSize: "0.8rem",
                                            }}
                                        >
                                            <div className="d-flex justify-content-between mb-1">
                                                <span style={{ color: styles.textMuted }}>Exchange rate</span>
                                                <span className="fw-semibold">1 USDT = {coinRate} Coins</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span style={{ color: styles.textMuted }}>Swap charges</span>
                                                <span className="fw-semibold" style={{ color: "#22c55e" }}>
                                                    0% (free)
                                                </span>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                borderRadius: "12px",
                                                padding: "12px 14px",
                                                backgroundColor: isDark ? "rgba(245, 158, 11, 0.12)" : "#fffbeb",
                                                border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.35)" : "#fde68a"}`,
                                                fontSize: "0.78rem",
                                                color: isDark ? "#fcd34d" : "#92400e",
                                            }}
                                            className="d-flex align-items-start gap-2"
                                        >
                                            <MdWarningAmber size={20} className="flex-shrink-0 mt-1" />
                                            <span>
                                                This action cannot be undone. Verify amounts before confirming.
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                            {!isProcessing && (
                                <div className="modal-footer border-0 px-4 pb-4 gap-2 d-flex">
                                    <button
                                        type="button"
                                        className="btn flex-grow-1"
                                        style={styles.outlineBtn}
                                        onClick={() => setShowConfirmModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn flex-grow-1 text-white"
                                        style={swapSubmitBtn}
                                        onClick={handleConfirmSwap}
                                    >
                                        Confirm swap
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}

            <style>{`
                .usdt-swap-amount-input::-webkit-inner-spin-button,
                .usdt-swap-amount-input::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .usdt-swap-amount-input {
                    -moz-appearance: textfield;
                }
            `}</style>
        </motion.div>
    );
};

export default UsdtSwap;
